import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { api } from "@/App";
import { useBrandLogos, brandLogoFor } from "@/lib/brandLogos";
import { Button } from "@/components/ui/button";

const BRANDS_PER_PAGE = 5;
const MODELS_STEP = 10;
const ENTRIES_STEP = 20;

export default function GroupedCompatibilityList({
  provider,
  params,
  renderRow,
  emptyText = "Keine Einträge gefunden.",
}) {
  const brandLogos = useBrandLogos();
  const [page, setPage] = useState(1);
  const [groups, setGroups] = useState([]);
  const [totalBrands, setTotalBrands] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const filterKey = JSON.stringify(params || {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableParams = useMemo(() => params || {}, [filterKey]);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/compatibility-groups/${provider}`, {
        params: {
          ...stableParams,
          mode: "brands",
          page,
          brands_per_page: BRANDS_PER_PAGE,
        },
      });
      setGroups(data.groups || []);
      setTotalBrands(data.total_brands || 0);
      setTotalEntries(data.total_entries || 0);
      setTotalPages(Math.max(1, data.total_pages || 1));
    } catch {
      setGroups([]);
      setTotalBrands(0);
      setTotalEntries(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [provider, stableParams, page]);

  useEffect(() => {
    setPage(1);
    setExpanded({});
  }, [provider, filterKey]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const loadMoreModels = async (brand) => {
    const group = groups.find((g) => g.name === brand);
    if (!group || group.models_loading) return;
    setGroups((current) => current.map((g) => g.name === brand ? { ...g, models_loading: true } : g));
    try {
      const { data } = await api.get(`/compatibility-groups/${provider}`, {
        params: {
          ...stableParams, mode: "models", brand,
          offset: group.models.length, limit: MODELS_STEP,
        },
      });
      setGroups((current) => current.map((g) => g.name === brand ? {
        ...g,
        models: [...g.models, ...(data.models || [])],
        models_total: data.total,
        models_loading: false,
      } : g));
    } catch {
      setGroups((current) => current.map((g) => g.name === brand ? { ...g, models_loading: false } : g));
    }
  };

  const modelKey = (brand, model) => `${brand}\u0000${model}`;

  const toggleModel = async (brand, model) => {
    const key = modelKey(brand, model);
    if (expanded[key]?.open) {
      setExpanded((current) => ({ ...current, [key]: { ...current[key], open: false } }));
      return;
    }
    if (expanded[key]?.items) {
      setExpanded((current) => ({ ...current, [key]: { ...current[key], open: true } }));
      return;
    }
    setExpanded((current) => ({ ...current, [key]: { open: true, loading: true, items: [], total: 0 } }));
    try {
      const { data } = await api.get(`/compatibility-groups/${provider}`, {
        params: { ...stableParams, mode: "entries", brand, model, offset: 0, limit: ENTRIES_STEP },
      });
      setExpanded((current) => ({ ...current, [key]: {
        open: true, loading: false, items: data.items || [], total: data.total || 0,
      } }));
    } catch {
      setExpanded((current) => ({ ...current, [key]: { open: true, loading: false, items: [], total: 0, error: true } }));
    }
  };

  const loadMoreEntries = async (brand, model) => {
    const key = modelKey(brand, model);
    const state = expanded[key];
    if (!state || state.loadingMore) return;
    setExpanded((current) => ({ ...current, [key]: { ...current[key], loadingMore: true } }));
    try {
      const { data } = await api.get(`/compatibility-groups/${provider}`, {
        params: {
          ...stableParams, mode: "entries", brand, model,
          offset: state.items.length, limit: ENTRIES_STEP,
        },
      });
      setExpanded((current) => ({ ...current, [key]: {
        ...current[key], loadingMore: false,
        items: [...current[key].items, ...(data.items || [])], total: data.total || 0,
      } }));
    } catch {
      setExpanded((current) => ({ ...current, [key]: { ...current[key], loadingMore: false } }));
    }
  };

  if (loading && groups.length === 0) {
    return <div className="rounded-xl border border-neutral-800 bg-panel py-16 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-neutral-500" /></div>;
  }

  if (!loading && groups.length === 0) {
    return <div className="rounded-xl border border-neutral-800 bg-panel px-4 py-16 text-center text-neutral-500">{emptyText}</div>;
  }

  return (
    <div className="space-y-5" data-testid={`${provider}-grouped-list`}>
      <div className="text-xs text-neutral-500">
        {totalEntries.toLocaleString("de-DE")} Einträge in {totalBrands.toLocaleString("de-DE")} Marken
      </div>
      {groups.map((group) => {
        const logo = brandLogoFor(brandLogos, group.name);
        return (
          <section key={group.name} className="rounded-xl border border-neutral-800 bg-panel overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-4 border-b border-neutral-800 bg-neutral-900/20">
              {logo && <img src={logo} alt="" className="w-12 h-12 object-contain rounded border border-neutral-800 p-1" />}
              <div className="min-w-0">
                <h2 className="font-semibold text-lg text-neutral-100 truncate">{group.name}</h2>
                <div className="text-xs text-neutral-500">{group.count.toLocaleString("de-DE")} Einträge · {group.models_total.toLocaleString("de-DE")} Modelle</div>
              </div>
            </div>
            <div className="divide-y divide-neutral-800/70">
              {group.models.map((model) => {
                const key = modelKey(group.name, model.name);
                const state = expanded[key];
                return (
                  <div key={model.name}>
                    <button
                      type="button"
                      onClick={() => toggleModel(group.name, model.name)}
                      aria-expanded={Boolean(state?.open)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-neutral-800/30 transition"
                    >
                      <ChevronDown className={`w-4 h-4 text-neutral-500 transition-transform ${state?.open ? "rotate-180" : ""}`} />
                      <span className="font-medium text-neutral-200 flex-1 truncate">{model.name}</span>
                      <span className="text-xs tabular-nums text-neutral-500">{model.count.toLocaleString("de-DE")}</span>
                    </button>
                    {state?.open && (
                      <div className="border-t border-neutral-800/70 bg-page/20">
                        {state.loading ? (
                          <div className="py-8 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-neutral-500" /></div>
                        ) : state.error ? (
                          <div className="px-4 py-6 text-sm text-rose-400">Einträge konnten nicht geladen werden.</div>
                        ) : (
                          <>
                            <div className="divide-y divide-neutral-800/60">
                              {state.items.map((row, index) => (
                                <div key={row.id || index}>{renderRow(row, { grouped: true })}</div>
                              ))}
                            </div>
                            {state.items.length < state.total && (
                              <button
                                type="button"
                                onClick={() => loadMoreEntries(group.name, model.name)}
                                disabled={state.loadingMore}
                                className="w-full px-4 py-3 text-sm text-[#D32222] hover:bg-neutral-800/30 disabled:opacity-50"
                              >
                                {state.loadingMore ? "Wird geladen…" : `20 weitere Einträge laden (${state.items.length.toLocaleString("de-DE")} von ${state.total.toLocaleString("de-DE")})`}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {group.models.length < group.models_total && (
              <button
                type="button"
                onClick={() => loadMoreModels(group.name)}
                disabled={group.models_loading}
                className="w-full px-4 py-3 border-t border-neutral-800 text-sm text-[#D32222] hover:bg-neutral-800/30 disabled:opacity-50"
              >
                {group.models_loading ? "Wird geladen…" : `Mehr Modelle anzeigen (${group.models.length.toLocaleString("de-DE")} von ${group.models_total.toLocaleString("de-DE")})`}
              </button>
            )}
          </section>
        );
      })}
      <div className="flex items-center justify-center gap-3 pt-2">
        <Button variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading} className="border-neutral-700 bg-panel hover:bg-neutral-800 text-neutral-100">
          <ChevronLeft className="w-4 h-4 mr-1" /> Zurück
        </Button>
        <span className="text-sm text-neutral-400">Markenseite <span className="text-neutral-100">{page}</span> / <span className="text-neutral-100">{totalPages}</span></span>
        <Button variant="outline" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading} className="border-neutral-700 bg-panel hover:bg-neutral-800 text-neutral-100">
          Weiter <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}
